import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Check, X, Clock, CurrencyDollar } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPurchasesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/purchases`);
      setPurchases(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/purchases/${id}/approve`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this purchase? No credits will be added.')) return;
    try {
      await axios.post(`${API}/admin/purchases/${id}/reject`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const filtered = purchases.filter(p => filter === 'all' || p.status === filter);

  const statusColors = {
    pending: 'bg-amber-400/10 text-amber-700 border-amber-200',
    approved: 'bg-lime-400/10 text-lime-300 border-lime-400/25',
    rejected: 'bg-red-400/10 text-red-400 border-red-200',
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="admin-purchases-page">
      <header className="h-14 border-b border-white/10 flex items-center px-6 gap-4">
        <button onClick={() => navigate('/app')} className="text-white/40 hover:text-white/80 transition-colors" data-testid="back-to-chat">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <CurrencyDollar className="w-5 h-5 text-lime-500" weight="bold" />
          <h1 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Credit Purchases</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-6">
        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                filter === f ? 'bg-gray-900 text-white' : 'bg-white/10 text-white/50 hover:bg-white/15'
              }`}>
              {f} {f !== 'all' && <span className="ml-1 opacity-60">({purchases.filter(p => p.status === f).length})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-white/40 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-white/40 text-sm">No {filter !== 'all' ? filter : ''} purchases</div>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => (
              <div key={p.id} className="border border-white/10 rounded-xl p-4 flex items-center gap-4" data-testid={`purchase-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-white">{p.user_name || p.user_email}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusColors[p.status] || ''}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="text-xs text-white/50 space-y-0.5">
                    <div>Amount: <span className="font-semibold text-white/80">${p.amount_usd}</span> → <span className="font-semibold text-white/80">{p.credits_to_add} credits</span></div>
                    <div>Cash App: <span className="font-semibold text-white/80">{p.cashapp_name}</span></div>
                    <div>Ref: <span className="font-mono text-white/70">{p.ref_code}</span></div>
                    <div className="flex items-center gap-1 text-white/40">
                      <Clock className="w-3 h-3" />
                      {new Date(p.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                {p.status === 'pending' && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}
                      className="w-9 h-9 rounded-lg bg-lime-400/100 hover:bg-lime-600 flex items-center justify-center text-white transition-colors">
                      <Check className="w-4 h-4" weight="bold" />
                    </button>
                    <button onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}
                      className="w-9 h-9 rounded-lg bg-red-400/10 hover:bg-red-400/20 flex items-center justify-center text-red-400 transition-colors border border-red-200">
                      <X className="w-4 h-4" weight="bold" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
